import React, { useState, useEffect, useCallback } from 'react';
import { priceBookAPI } from '../lib/api';
import { Layout } from './Layout';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Search, Loader2, BookOpen, Package, AlertCircle, RefreshCw, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Read-only HCP Pricebook view for inventory-only + HCP-connected tenants.
 * Shows materials synced from Housecall Pro with HCP source pill.
 * No create/edit/delete actions.
 */
export default function HCPPricebookView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await priceBookAPI.getHcpPricebook({ q: search, page, page_size: pageSize });
      setData(resp.data);
    } catch (err) {
      console.error('Failed to fetch HCP pricebook:', err);
      toast.error('Failed to load pricebook data');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const hcpConnected = data?.hcp_connected ?? false;
  const materialsAvailable = data?.materials_available ?? false;
  const lastSync = data?.last_sync;
  const totalPages = Math.ceil(total / pageSize);

  // Format currency
  const fmtPrice = (v) => v != null ? `$${Number(v).toFixed(2)}` : '—';

  // Determine the state to render
  const renderContent = () => {
    if (loading && !data) {
      return (
        <div className="flex items-center justify-center py-24" data-testid="hcp-pb-loading">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    // State: HCP not connected
    if (!hcpConnected) {
      return (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center" data-testid="hcp-pb-not-connected">
          <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <BookOpen className="w-7 h-7 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200">No Pricebook Connected</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-md">
            Connect Housecall Pro in Settings to sync your pricebook here. Your HCP materials and services will appear as a read-only reference.
          </p>
        </div>
      );
    }

    // State: HCP connected but materials API not available
    if (!materialsAvailable && items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center" data-testid="hcp-pb-unavailable">
          <div className="w-14 h-14 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-4">
            <AlertCircle className="w-7 h-7 text-amber-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200">Pricebook Data Unavailable</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-md">
            Your Housecall Pro account is connected, but the pricebook/materials API is not available with your current HCP plan or API access level.
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md">
            Jobs may sync normally while pricebook data requires additional HCP API access. Contact Housecall Pro support to verify your plan includes pricebook API access.
          </p>
          {lastSync?.error && (
            <div className="mt-4 px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-xs text-slate-500 max-w-md">
              Last attempt: {lastSync.error}
            </div>
          )}
        </div>
      );
    }

    // State: HCP connected, materials available, but 0 items after search
    if (items.length === 0 && search) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Search className="w-8 h-8 text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No items match "{search}"</p>
        </div>
      );
    }

    // State: HCP connected, materials available, 0 items (no search)
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="hcp-pb-empty">
          <Package className="w-8 h-8 text-slate-300 mb-3" />
          <h3 className="text-base font-medium text-slate-600">No Pricebook Items Yet</h3>
          <p className="text-sm text-slate-400 mt-1">Run a Full Import from your HCP settings to sync pricebook data.</p>
        </div>
      );
    }

    // State: Items exist — show the table
    return (
      <>
        {/* Desktop table */}
        <div className="hidden sm:block border rounded-lg overflow-hidden" data-testid="hcp-pb-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[45%]">Item Name</TableHead>
                <TableHead className="w-[20%]">SKU</TableHead>
                <TableHead className="w-[15%] text-right">Unit Price</TableHead>
                <TableHead className="w-[20%]">Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid={`hcp-pb-row-${item.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-medium truncate">{item.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500 font-mono text-sm">{item.sku || '—'}</TableCell>
                  <TableCell className="text-right font-medium">{fmtPrice(item.unit_price)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800" data-testid="hcp-source-pill">
                      <ExternalLink className="w-3 h-3 mr-1" />HCP
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="sm:hidden space-y-2" data-testid="hcp-pb-mobile-cards">
          {items.map((item) => (
            <div key={item.id} className="border rounded-lg p-3 flex items-center justify-between" data-testid={`hcp-pb-card-${item.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-sm truncate">{item.name}</span>
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0 px-1 py-0">HCP</Badge>
                </div>
                {item.sku && <p className="text-xs text-slate-400 font-mono mt-0.5">{item.sku}</p>}
              </div>
              <span className="font-semibold text-sm ml-2 shrink-0">{fmtPrice(item.unit_price)}</span>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 text-sm">
            <span className="text-slate-500">{total} item{total !== 1 ? 's' : ''}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <span className="px-2 py-1 text-slate-600">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8" data-testid="hcp-pricebook-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Price Book</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {hcpConnected ? 'Synced from Housecall Pro — read-only' : 'Connect Housecall Pro to view your pricebook'}
            </p>
          </div>
          {hcpConnected && materialsAvailable && (
            <Button variant="outline" size="sm" onClick={() => fetchData()} disabled={loading} data-testid="hcp-pb-refresh">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>

        {/* Last sync info */}
        {lastSync && hcpConnected && (
          <div className="mb-4 text-xs text-slate-400 flex items-center gap-1.5" data-testid="hcp-pb-sync-info">
            Last synced: {lastSync.completed_at ? new Date(lastSync.completed_at).toLocaleString() : 'Never'}
            {lastSync.status === 'failed' && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 ml-1">Sync Failed</Badge>}
            {lastSync.status === 'success' && lastSync.fetched > 0 && <Badge variant="outline" className="text-[10px] text-green-600 border-green-300 ml-1">{lastSync.fetched} items</Badge>}
          </div>
        )}

        {/* Search — only show when there are items or when HCP is connected + available */}
        {hcpConnected && materialsAvailable && (items.length > 0 || search) && (
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search pricebook items..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
              data-testid="hcp-pb-search"
            />
          </div>
        )}

        {renderContent()}
      </div>
    </Layout>
  );
}
